import { useState, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Building2, Users, LayoutDashboard, LogOut, ChevronDown, Shield, KeyRound, Wrench, Table, Activity, Database, UserCheck, Clock } from "lucide-react";
import { TipoCartaoModal, ClassificacaoModal, FuncoesModal, ParentescosModal, EventoMsgModal } from "@/pages/admin/TabelasPublicasModals";
import { APP_VERSION } from "@/lib/version";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import { ContentFallback } from "@/components/RouteFallback";

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [cadastrosOpen, setCadastrosOpen] = useState(false);
  const [manutencaoOpen, setManutencaoOpen] = useState(false);
  const [sincronizacaoOpen, setSincronizacaoOpen] = useState(false);
  const [showTipoCartaoModal, setShowTipoCartaoModal] = useState(false);
  const [showClassificacaoModal, setShowClassificacaoModal] = useState(false);
  const [showFuncoesModal, setShowFuncoesModal] = useState(false);
  const [showParentescosModal, setShowParentescosModal] = useState(false);
  const [showEventoMsgModal, setShowEventoMsgModal] = useState(false);
  const user = JSON.parse(sessionStorage.getItem("brave_user") || "{}");

  const handleLogout = () => {
    sessionStorage.removeItem("brave_token");
    sessionStorage.removeItem("brave_user");
    navigate("/admin");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-page-gradient">
      {/* Top Navigation */}
      <nav
        data-testid="main-nav"
        className="sticky top-0 z-50 w-full bg-nav-gradient shadow-lg"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <button
              data-testid="nav-logo"
              onClick={() => navigate("/admin/dashboard")}
              className="flex items-center gap-2 text-white"
            >
              <Shield className="w-6 h-6 text-brand-light" />
              <span className="font-heading font-extrabold text-lg tracking-tight text-shadow-logo">
                SGA Brave Access
              </span>
            </button>

            {/* Menu items */}
            <div className="flex items-center gap-1">
              {/* Dashboard */}
              <button
                data-testid="nav-dashboard"
                onClick={() => navigate("/admin/dashboard")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive("/admin/dashboard")
                    ? "bg-white/15 text-white"
                    : "text-brand-ice hover:bg-white/10 hover:text-white"
                }`}
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </button>

              {/* Cadastros Dropdown */}
              <div
                className="relative nav-dropdown-container"
                onMouseEnter={() => setCadastrosOpen(true)}
                onMouseLeave={() => setCadastrosOpen(false)}
              >
                <button
                  data-testid="nav-cadastros"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    location.pathname.includes("/clientes") || location.pathname.includes("/usuarios")
                      ? "bg-white/15 text-white"
                      : "text-brand-ice hover:bg-white/10 hover:text-white"
                  }`}
                >
                  Cadastros
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${
                      cadastrosOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {cadastrosOpen && (
                  <div
                    data-testid="cadastros-dropdown"
                    className="absolute top-full left-0 mt-0 w-48 bg-brand-dark/95 backdrop-blur-md border border-brand-medium/30 rounded-lg shadow-xl overflow-hidden"
                    style={{ paddingTop: 0 }}
                  >
                    <button
                      data-testid="nav-clientes"
                      onClick={() => {
                        navigate("/admin/clientes");
                        setCadastrosOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                        location.pathname.includes("/clientes")
                          ? "bg-white/15 text-white"
                          : "text-brand-ice hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <Building2 className="w-4 h-4" />
                      Clientes
                    </button>
                    <button
                      data-testid="nav-usuarios"
                      onClick={() => {
                        navigate("/admin/usuarios");
                        setCadastrosOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                        location.pathname.includes("/usuarios")
                          ? "bg-white/15 text-white"
                          : "text-brand-ice hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <Users className="w-4 h-4" />
                      Usuários
                    </button>
                    <button
                      data-testid="nav-api-keys"
                      onClick={() => {
                        navigate("/admin/api-keys");
                        setCadastrosOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                        location.pathname.includes("/api-keys")
                          ? "bg-white/15 text-white"
                          : "text-brand-ice hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <KeyRound className="w-4 h-4" />
                      Chaves de API
                    </button>
                  </div>
                )}
              </div>

              {/* Manutenção Dropdown */}
              <div
                className="relative nav-dropdown-container"
                onMouseEnter={() => setManutencaoOpen(true)}
                onMouseLeave={() => setManutencaoOpen(false)}
              >
                <button
                  data-testid="nav-manutencao"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 text-brand-ice hover:bg-white/10 hover:text-white`}
                >
                  <Wrench className="w-4 h-4" />
                  Manutenção
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${manutencaoOpen ? "rotate-180" : ""}`} />
                </button>

                {manutencaoOpen && (
                  <div
                    data-testid="manutencao-dropdown"
                    className="absolute top-full left-0 mt-0 w-52 bg-brand-dark/95 backdrop-blur-md border border-brand-medium/30 rounded-lg shadow-xl overflow-hidden"
                  >
                    <div className="px-4 py-2 border-b border-brand-ice/10">
                      <span className="text-[10px] uppercase tracking-wider text-brand-ice/50 font-bold">Tabelas Públicas</span>
                    </div>
                    <button
                      data-testid="nav-tipo-cartao"
                      onClick={() => { setShowTipoCartaoModal(true); setManutencaoOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-ice hover:bg-white/10 hover:text-white transition-colors duration-150"
                    >
                      <Table className="w-4 h-4" />
                      Tipo de Cartão
                    </button>
                    <button
                      data-testid="nav-classificacao"
                      onClick={() => { setShowClassificacaoModal(true); setManutencaoOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-ice hover:bg-white/10 hover:text-white transition-colors duration-150"
                    >
                      <Table className="w-4 h-4" />
                      Classificação
                    </button>
                    <button
                      data-testid="nav-funcoes"
                      onClick={() => { setShowFuncoesModal(true); setManutencaoOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-ice hover:bg-white/10 hover:text-white transition-colors duration-150"
                    >
                      <Table className="w-4 h-4" />
                      Funções
                    </button>
                    <button
                      data-testid="nav-parentescos"
                      onClick={() => { setShowParentescosModal(true); setManutencaoOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-ice hover:bg-white/10 hover:text-white transition-colors duration-150"
                    >
                      <Table className="w-4 h-4" />
                      Parentescos
                    </button>
                    <button
                      data-testid="nav-eventomsg"
                      onClick={() => { setShowEventoMsgModal(true); setManutencaoOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-ice hover:bg-white/10 hover:text-white transition-colors duration-150"
                    >
                      <Table className="w-4 h-4" />
                      Evento Msg
                    </button>
                    <div className="border-t border-brand-ice/15 my-1" />
                    <button
                      data-testid="nav-backup-publico"
                      onClick={() => { navigate("/admin/manutencao/backup-publico"); setManutencaoOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-ice hover:bg-white/10 hover:text-white transition-colors duration-150"
                    >
                      <Database className="w-4 h-4" />
                      Backup DB Público
                    </button>
                    <button
                      data-testid="nav-veiculos-orfaos"
                      onClick={() => { navigate("/admin/manutencao/veiculos-orfaos"); setManutencaoOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-ice hover:bg-white/10 hover:text-white transition-colors duration-150"
                    >
                      <Table className="w-4 h-4" />
                      Veículos Adicionais Órfãos
                    </button>
                  </div>
                )}
              </div>

              {/* Sincronização Dropdown */}
              <div
                className="relative nav-dropdown-container"
                onMouseEnter={() => setSincronizacaoOpen(true)}
                onMouseLeave={() => setSincronizacaoOpen(false)}
              >
                <button
                  data-testid="nav-sincronizacao"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    location.pathname.includes("/admin/sync")
                      ? "bg-white/15 text-white"
                      : "text-brand-ice hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Database className="w-4 h-4" />
                  Sincronização
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${sincronizacaoOpen ? "rotate-180" : ""}`} />
                </button>

                {sincronizacaoOpen && (
                  <div
                    data-testid="sincronizacao-dropdown"
                    className="absolute top-full left-0 mt-0 w-56 bg-brand-dark/95 backdrop-blur-md border border-brand-medium/30 rounded-lg shadow-xl overflow-hidden"
                  >
                    <button
                      data-testid="nav-sync-clientes"
                      onClick={() => { navigate("/admin/sync/diagnostico"); setSincronizacaoOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                        isActive("/admin/sync/diagnostico")
                          ? "bg-white/15 text-white"
                          : "text-brand-ice hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <Activity className="w-4 h-4" />
                      Clientes
                    </button>
                    <button
                      data-testid="nav-sync-cadastros"
                      onClick={() => { navigate("/admin/sync/cadastros/moradores"); setSincronizacaoOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                        isActive("/admin/sync/cadastros/moradores")
                          ? "bg-white/15 text-white"
                          : "text-brand-ice hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <UserCheck className="w-4 h-4" />
                      Cadastros
                    </button>
                    <button
                      data-testid="nav-sync-setores"
                      onClick={() => { navigate("/admin/sync/cadastros/setores"); setSincronizacaoOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                        isActive("/admin/sync/cadastros/setores")
                          ? "bg-white/15 text-white"
                          : "text-brand-ice hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <Building2 className="w-4 h-4" />
                      Setores
                    </button>
                    <button
                      data-testid="nav-sync-horarios"
                      onClick={() => { navigate("/admin/sync/cadastros/horarios"); setSincronizacaoOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 ${
                        isActive("/admin/sync/cadastros/horarios")
                          ? "bg-white/15 text-white"
                          : "text-brand-ice hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <Clock className="w-4 h-4" />
                      Horários
                    </button>
                  </div>
                )}
              </div>

              {/* Separator */}
              <div className="w-px h-6 bg-white/20 mx-2" />

              {/* User info */}
              <span className="text-brand-ice text-xs font-medium mr-2 hidden sm:block">
                {user?.apelido || user?.nome || ""}
              </span>

              {/* Logout */}
              <button
                data-testid="nav-logout"
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-300 hover:bg-red-500/15 hover:text-red-200 transition-colors duration-200"
              >
                <LogOut className="w-4 h-4" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <RouteErrorBoundary>
          <Suspense fallback={<ContentFallback />}>
            {children}
          </Suspense>
        </RouteErrorBoundary>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center">
        <a
          href="https://www.braveaccess.com.br"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-brave-link"
          style={{ textDecoration: "none" }}
          className="text-brand-ice/60 hover:text-brand-light text-xs font-body text-shadow-footer transition-colors no-underline block"
          title="Brave Access"
        >
          SGA Brave Access &copy; {new Date().getFullYear()} &mdash; Desenvolvido por Interaccess Sistemas
        </a>
        <p className="text-brand-ice/50 text-xs font-heading font-bold tracking-wider text-shadow-footer">
          SGA - Sistema de Gerenciamento de Acesso <span className="text-brand-ice/35">- Versão {APP_VERSION}</span>
        </p>
      </footer>

      {/* Modais de Tabelas Públicas */}
      <TipoCartaoModal open={showTipoCartaoModal} onClose={() => setShowTipoCartaoModal(false)} />
      <ClassificacaoModal open={showClassificacaoModal} onClose={() => setShowClassificacaoModal(false)} />
      <FuncoesModal open={showFuncoesModal} onClose={() => setShowFuncoesModal(false)} />
      <ParentescosModal open={showParentescosModal} onClose={() => setShowParentescosModal(false)} />
      <EventoMsgModal open={showEventoMsgModal} onClose={() => setShowEventoMsgModal(false)} />
    </div>
  );
}
